/**
 * This file is a hack to circumvent issues exposing browser side environment variables.
 *
 * NextJS compiles the underlying JS files that make up our app statically before our image is deployed.
 * As a result, we don't actually have access to any server side variables we set in Kubernetes. Since
 * we don't have access to process.env, we have to check the URL with window.location.href to determine
 * which environment we're in. Although this is not ideal, it is the best solution we have found so far.
 */

const SERVER_ENV_MAP = {
    development: 'staging',
    production: 'prod',
    test: 'dev',
};

export type Env = 'dev' | 'staging' | 'prod';

export const getOrigin = (): string => (typeof window !== 'undefined' ? window.location.origin : '');

export const hostEndsWithAny = (urlHost: string, hosts: string[]): boolean => {
    return hosts.some(host => {
        return urlHost.endsWith(host);
    });
};

export const getEnv = (): Env => {
    // if server side, return env var
    if (typeof window === 'undefined') {
        return (process.env.NEXT_PUBLIC_STAGE ?? 'dev') as Env;
    }

    const host = new URL(window.location.href).host;

    const allowedHosts = {
        prod: ['datavant.com', 'smartrequest.com'],
        staging: ['datavant-staging.com', 'datavant-staging.com'],
        dev: [
            'localhost:3000',
            // Docker "localhost"
            'host.docker.internal:3000',
            // Docker service used for CI
            'idsb-portal-service:3000',
        ],
    };

    if (hostEndsWithAny(host, allowedHosts.prod)) {
        return 'prod';
    } else if (hostEndsWithAny(host, allowedHosts.staging)) {
        return 'staging';
    } else if (hostEndsWithAny(host, allowedHosts.dev)) {
        return 'dev';
    } else {
        throw new Error('Invalid URL: ' + window.location.href + ' from host ' + host);
    }
};

export const ENV_VARS = {
    dev: {
        datadog: {
            enabled: false,
            allowedTracingUrls: ['http://localhost'],
        },
        launchDarklySdkClientSideId: '63d908b12acbb412c696c577',
        idsbPortalOkta: {
            issuer: 'https://cioxapps.oktapreview.com/oauth2/default',
            clientId: '0oa1pivy1c1Y773Ks0h8',
            redirectUri: `http://localhost:3000/cb`,
            postLogoutRedirectUri: `http://localhost:3000/login`,
        },
        okta: {
            issuer: 'https://cioxapps.oktapreview.com/oauth2/default',
            clientId: '0oa1pivy1c1Y773Ks0h8',
            redirectUri: `${getOrigin()}/cb`,
            postLogoutRedirectUri: `${getOrigin()}/login`,
        },
        cybersource: {
            smartRequestIframeUrl: 'https://testsecureacceptance.cybersource.com/embedded/pay',
        }
    },
    staging: {
        datadog: {
            enabled: true,
            allowedTracingUrls: ['https://sr.datavant-staging.com'],
        },
        launchDarklySdkClientSideId: '63d908b12acbb412c696c577',
        idsbPortalOkta: {
            issuer: 'https://cioxapps.oktapreview.com/oauth2/default',
            clientId: '0oa1pivy1c1Y773Ks0h8',
            redirectUri: `https://idsb-portal.datavant-staging.com/cb`,
            postLogoutRedirectUri: `https://idsb-portal.datavant-staging.com/login`,
        },
        okta: {
            issuer: 'https://cioxapps.oktapreview.com/oauth2/default',
            clientId: '0oa1pivy1c1Y773Ks0h8',
            redirectUri: `${getOrigin()}/cb`,
            postLogoutRedirectUri: `${getOrigin()}/login`,
        },
        cybersource: {
            smartRequestIframeUrl: 'https://testsecureacceptance.cybersource.com/embedded/pay',
        }
    },
    prod: {
        datadog: {
            enabled: true,
            allowedTracingUrls: [
                'https://app.smartrequest.com',
                'https://smartrequest.com',
                'https://www.smartrequest.com',
                'https://sr.datavant.com',
            ],
        },
        launchDarklySdkClientSideId: '63d908b12acbb412c696c578',
        idsbPortalOkta: {
            issuer: 'https://cioxapps.okta.com/oauth2/default',
            clientId: '0oat6qv55iheIRBVU357',
            redirectUri: `https://idsb-portal.datavant.com/cb`,
            postLogoutRedirectUri: `https://idsb-portal.datavant.com/login`,
        },
        okta: {
            issuer: 'https://cioxapps.okta.com/oauth2/default',
            clientId: '0oat6qv55iheIRBVU357',
            redirectUri: `${getOrigin()}/cb`,
            postLogoutRedirectUri: `${getOrigin()}/login`,
        },
        cybersource: {
            smartRequestIframeUrl: 'https://secureacceptance.cybersource.com/embedded/pay',
        }
    },
};

export const getEnvVar = <T extends Env, K extends keyof (typeof ENV_VARS)[T]>(env: T, key: K) => {
    return ENV_VARS[env][key];
};
